import {
  createFileRoute,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router';
import { useDeferredValue, useMemo, useState } from 'react';
import { logspot } from '@logspot/web';
import {
  PencilSimpleIcon,
  PlusCircleIcon,
  ShareIcon,
} from '@phosphor-icons/react';
import { useSuspenseQueries } from '@tanstack/react-query';
import { WidgetsProvider } from '@logspot-platform/widgets';
import { AddWidgetModal } from '@/components/widgets/add-widget-modal';
import { DashboardV2 } from '@/components/dashboard/dashboard-v2';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/date-picker';
import { Filters } from '@/components/filters';
import { useProjects } from '@/hooks/use-projects';
import { FilterDialogButton } from '@/components/filter-dialog-button';
import { OnlineNowBadge } from '@/components/dashboard/online-now-badge';
import { useFilters } from '@/hooks/use-filters';
import { DashboardNoEvents } from '@/components/dashboard-no-events';
import { NoMatchingEvents } from '@/components/no-matching-events';
import { WidgetGrid } from '@/components/widgets/widget-grid';
import { useDateRange } from '@/hooks/use-date-range';
import { PageContent } from '@/components/page-content';
import {
  allEventsOptions,
  allEventsPrevOptions,
  dashboardConfigOptions,
  eventNamesOptions,
  previousDateRange,
} from '@/hooks/query-options';
import { useFeatures } from '@/hooks/use-features';
import { hasPermission } from '@/lib/permissions';
import { IdentificationPausedNotice } from '@/components/billing/identification-paused-notice';
import type { FilterGroup } from '@/types';

export const Route = createFileRoute('/_auth/_side-layout/')({
  component: Dashboard,
});

function Dashboard() {
  const { projectId, projectName } = useProjects();
  const {
    filterGroups: liveFilterGroups,
    addFilter,
    isActiveFilter,
  } = useFilters();
  const { userRole } = useRouteContext({ from: '/_auth' });
  const canEditProject = hasPermission(userRole, { project: ['update'] });
  const [liveDateRange] = useDateRange();
  // Keeps the current page up while a new range or filter loads; suspending swapped in
  // the fallback, which collapsed the page and dropped the scroll position to the top.
  const dateRange = useDeferredValue(liveDateRange);
  const filterKey = useDeferredValue(JSON.stringify(liveFilterGroups));
  const filterGroups = useMemo(
    () => JSON.parse(filterKey) as FilterGroup[],
    [filterKey],
  );
  const navigate = useNavigate();
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  // Edit mode belongs to the project it started on, so switching projects leaves it.
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const editing = !!projectId && editingProject === projectId;
  const { isEnabled } = useFeatures();
  const v2 = isEnabled('dashboardV2');
  const prevDateRange = previousDateRange(dateRange);

  const { data } = useSuspenseQueries({
    queries: [
      allEventsOptions({
        projectId: projectId!,
        dateRange,
        filterGroups,
      }),
      allEventsPrevOptions({
        projectId: projectId!,
        dateRange,
        filterGroups,
      }),
      dashboardConfigOptions({ projectId: projectId! }),
      // All-time event names (no date filter) — tells us whether the project
      // has *ever* received an event, independent of the selected range.
      // Already prefetched by the _side-layout loader. Override refetchOnMount
      // to match allEventsOptions: the shared helper's 1h staleTime is fine for
      // dropdowns, but the onboarding gate must react to a project's first
      // event on the next navigation instead of needing a hard refresh.
      {
        ...eventNamesOptions({ projectId: projectId! }),
        refetchOnMount: 'always' as const,
      },
    ],
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
      };
    },
  });

  const [events, prevEvents, dashConfig, allEventNames] = data as [
    Record<string, import('@/types').DayMetrics[]>,
    Record<string, import('@/types').DayMetrics[]>,
    import('@/types').DashboardConfig,
    { name: string }[],
  ];

  const eventNames = events ? Object.keys(events) : [];

  const hasEvents = eventNames.length > 0;
  const hasAnyEvents = (allEventNames?.length ?? 0) > 0;
  const hasFilters = !!filterGroups[0]?.filters.length;

  // Onboarding state only when the project has genuinely never received an
  // event — not merely when the selected date range (e.g. last 24h) is empty.
  // An empty range on a project with data falls through to <NoMatchingEvents />
  // below, which keeps the date picker visible so the user can widen the range.
  if (!hasAnyEvents && !hasFilters) {
    return <DashboardNoEvents projectName={projectName || 'Your Project.'} />;
  }

  return (
    <PageContent>
      {/* <PageHeader title="Dashboard" description="Monitor your project's events, users, and key metrics." /> */}
      <IdentificationPausedNotice projectId={projectId} />
      {canEditProject && (
        <AddWidgetModal
          isOpen={addWidgetOpen}
          onClose={() => setAddWidgetOpen(false)}
        />
      )}
      <div className="flex justify-between">
        <div className="flex w-full flex-col items-start gap-2 md:w-auto md:flex-row md:items-center">
          <DatePicker />
          <div>
            <FilterDialogButton />
          </div>
          {v2 && hasEvents && projectId && (
            <OnlineNowBadge
              dateRange={dateRange}
              filterGroups={filterGroups}
              projectId={projectId}
            />
          )}
        </div>
        {(v2 || canEditProject) && (
          <div className="flex flex-col items-end gap-2 md:flex-row md:items-center">
            {/* Save for Me is open to every role, so the new dashboard's editor is too. */}
            {v2 && hasEvents && !editing && (
              <Button
                iconLeft={<PencilSimpleIcon />}
                variant="outline"
                onClick={() => {
                  logspot.track({ event: 'Dashboard Edit Opened' });
                  setEditingProject(projectId ?? null);
                }}
              >
                Edit Dashboard
              </Button>
            )}
            {!v2 && (
              <Button
                iconLeft={<PlusCircleIcon />}
                variant="outline"
                onClick={() => {
                  logspot.track({ event: 'Widget Added' });
                  return setAddWidgetOpen(true);
                }}
              >
                Add Widget
              </Button>
            )}
            {canEditProject && (
              <Button
                iconLeft={<ShareIcon />}
                variant="outline"
                onClick={() => {
                  logspot.track({ event: 'Dashboard Share Opened' });
                  void navigate({ to: '/dashboard-share' });
                }}
              >
                Share
              </Button>
            )}
          </div>
        )}
      </div>
      <Filters />
      <div>
        {!hasEvents ? (
          <NoMatchingEvents />
        ) : v2 ? (
          <WidgetsProvider
            addFilter={addFilter}
            api={api}
            filterGroups={filterGroups}
            isActiveFilter={(filter) => !!isActiveFilter(filter)}
            projectId={projectId!}
          >
            <DashboardV2
              key={projectId}
              canEdit={canEditProject}
              editing={editing}
              onEditingChange={(next) =>
                setEditingProject(next ? (projectId ?? null) : null)
              }
              dashConfig={dashConfig}
              dateRange={dateRange}
              events={events}
              filterGroups={filterGroups}
              onAddWidget={() => setAddWidgetOpen(true)}
              prevDateRange={prevDateRange}
              prevEvents={prevEvents}
              projectId={projectId!}
            />
          </WidgetsProvider>
        ) : (
          <WidgetsProvider
            addFilter={addFilter}
            api={api}
            filterGroups={filterGroups}
            isActiveFilter={(filter) => !!isActiveFilter(filter)}
            projectId={projectId!}
          >
            <WidgetGrid
              canEditProject={canEditProject}
              dashData={dashConfig}
              dateRange={dateRange}
              eventData={events}
              eventNames={eventNames}
              prevData={prevEvents}
              prevDateRange={prevDateRange}
              projectId={projectId!}
            />
          </WidgetsProvider>
        )}
      </div>
    </PageContent>
  );
}
